import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles } from "lucide-react";
import { Button, cn } from "@eduviaos/ui-kit";
import { useCompleteFirstRun } from "@/hooks/useFirstRun";

/**
 * Phase C1b — IA first-run wizard shell.
 *
 * Wraps every wizard page so the welcome / invite-staff / future steps
 * share one chrome: a top progress strip + a "Skip & finish setup" exit
 * affordance + the gradient backdrop the welcome step established. The
 * pages claim the full viewport (no AppShell sidebar / topbar) by living
 * outside the AppShell route in `App.tsx`.
 *
 * Step rendering rule: the wizard shows ONE label for the current step
 * plus tiny dot indicators for the other declared steps. We deliberately
 * keep labels off non-active steps until later phases ship them — a row
 * of grey labels that don't go anywhere reads as a list of broken
 * promises. Once C1c+ land, we light them up.
 */

export interface FirstRunStepDescriptor {
  /** Stable key so the rail can highlight the right dot. */
  key: string;
  /** Shown next to the active dot. Other dots show no label. */
  label: string;
  /** ``true`` once the principal has navigated past this step. */
  done?: boolean;
}

export interface FirstRunShellProps {
  steps: FirstRunStepDescriptor[];
  activeStep: string;
  /** Optional eyebrow rendered above the page title (e.g. "Step 2 of 4"). */
  eyebrow?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Visibility of the "Skip & finish setup" exit. Defaults to ``true``
   * on every step but the welcome step (welcome's primary CTA is the
   * forward-only "Continue", and a sibling "Skip" would dilute it).
   */
  showSkipExit?: boolean;
}

export function FirstRunShell({
  steps,
  activeStep,
  eyebrow,
  children,
  showSkipExit = true,
}: FirstRunShellProps) {
  const navigate = useNavigate();
  const complete = useCompleteFirstRun();

  const handleSkip = () => {
    complete.mutate(undefined, {
      onSuccess: () => {
        // Match the welcome step's 200ms breathing room before redirect
        // so the success state isn't a flash. See `FirstRunWelcomePage`
        // for the same rationale.
        setTimeout(() => navigate("/dashboard", { replace: true }), 200);
      },
    });
  };

  return (
    <main className="from-background via-background to-muted/40 relative min-h-svh overflow-hidden bg-gradient-to-b">
      <div
        aria-hidden="true"
        className="from-primary/15 via-primary/5 pointer-events-none absolute -top-40 left-1/2 size-[40rem] -translate-x-1/2 rounded-full bg-gradient-to-b to-transparent blur-3xl"
      />

      <div className="relative mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-4 pt-6 sm:px-6 sm:pt-8">
        <div className="bg-card text-muted-foreground inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em]">
          <Sparkles className="size-3.5" aria-hidden="true" />
          {eyebrow ?? "First-run setup"}
        </div>
        <StepRail steps={steps} activeStep={activeStep} />
        {showSkipExit ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            disabled={complete.isPending}
            className="text-muted-foreground hover:text-foreground"
          >
            {complete.isPending ? (
              <>
                <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden="true" />
                Finishing…
              </>
            ) : (
              "Skip & finish setup"
            )}
          </Button>
        ) : (
          <span className="w-[140px]" aria-hidden="true" />
        )}
      </div>

      <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-14">{children}</div>
    </main>
  );
}

interface StepRailProps {
  steps: FirstRunStepDescriptor[];
  activeStep: string;
}

function StepRail({ steps, activeStep }: StepRailProps) {
  const activeIndex = Math.max(
    0,
    steps.findIndex((s) => s.key === activeStep),
  );
  const activeLabel = steps[activeIndex]?.label ?? "";
  return (
    <div role="list" aria-label="Setup progress" className="hidden items-center gap-3 sm:flex">
      {steps.map((step, idx) => {
        const isActive = idx === activeIndex;
        const isDone = step.done || idx < activeIndex;
        return (
          <div
            key={step.key}
            role="listitem"
            aria-current={isActive ? "step" : undefined}
            className={cn(
              "flex items-center gap-2 transition-colors duration-200",
              isActive ? "text-foreground" : "text-muted-foreground/70",
            )}
          >
            <span
              className={cn(
                "grid size-5 place-items-center rounded-full border text-[10px] font-semibold transition-colors duration-200",
                isActive
                  ? "border-primary bg-primary text-primary-foreground"
                  : isDone
                    ? "border-primary/50 bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground/80",
              )}
            >
              {isDone ? <Check className="size-3" aria-hidden="true" /> : idx + 1}
            </span>
            {isActive ? <span className="text-xs font-medium">{activeLabel}</span> : null}
          </div>
        );
      })}
    </div>
  );
}
